import { useEffect, useState } from "react";
import { Coins } from "@phosphor-icons/react";
import { EmptyState } from "../components/shared/EmptyState";
import { BigMoneyFilters } from "../components/kalshi/BigMoneyFilters";
import { BigMoneyTable } from "../components/kalshi/BigMoneyTable";
import { getKalshiBigMoney, getKalshiTwoSidedWarnings } from "../lib/api";

const REMOVAL_CHECK_MS = 15000;

const KalshiBigMoney = () => {
  const [minDollars, setMinDollars] = useState(500);
  const [side, setSide] = useState("ALL");
  const [sort, setSort] = useState("largest");
  const [sport, setSport] = useState("ALL");
  const [actionableOnly, setActionableOnly] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getKalshiBigMoney({ min_dollars: minDollars, side, sort, sport, actionable_only: actionableOnly })
      .then((res) => {
        setData(res);
        setRows(res.trades || []);
      })
      .catch(() => {
        setData(null);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [minDollars, side, sort, sport, actionableOnly]);

  useEffect(() => {
    getKalshiTwoSidedWarnings({ min_dollars: minDollars })
      .then((res) => setWarnings(res.warnings || []))
      .catch(() => setWarnings([]));
  }, [minDollars]);

  // Removes a row the instant its verified event start passes -- a pure client-side re-filter
  // of rows already fetched, never an extra network request, never a page reload.
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      setRows((prev) => prev.filter((r) => !r.start_verified || new Date(r.verified_start_utc).getTime() > now));
    }, REMOVAL_CHECK_MS);
    return () => clearInterval(id);
  }, []);

  const hiddenUnknownCount = data?.hidden_unknown_start_count || 0;

  return (
    <div data-testid="kalshi-big-money-page" className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Kalshi Big Money</h1>
        <p className="mt-1 font-mono text-xs text-zinc-500">
          Large executed trades on Kalshi's public markets, from Kalshi's official read-only trade data -- market
          intelligence only. Shows UPCOMING single-market and parlay trades only: once a game (or, for a parlay, any
          one of its legs) starts, or the market closes/settles, the trade drops off this list automatically -- it
          stays in EdgeBall's own trade history regardless. Kalshi does not expose trader identity, so no trade is
          ever attributed to a specific user, whale, or "sharp" bettor. Never feeds EdgeBall's own predictions, Full
          Game O/U, F5 O/U, NRFI/YRFI, or Largest Leans.
        </p>
      </div>

      <BigMoneyFilters
        minDollars={minDollars}
        onMinDollarsChange={setMinDollars}
        side={side}
        onSideChange={setSide}
        sort={sort}
        onSortChange={setSort}
        sport={sport}
        onSportChange={setSport}
        actionableOnly={actionableOnly}
        onActionableOnlyChange={setActionableOnly}
      />

      {loading ? (
        <p data-testid="kalshi-loading" className="font-mono text-xs text-zinc-500">Loading Kalshi trades...</p>
      ) : !data ? (
        <EmptyState icon={Coins} title="Kalshi Big Money Unavailable" description="Could not load Kalshi trade data." testId="kalshi-empty" />
      ) : (
        <>
          <BigMoneyTable rows={rows} warnings={warnings} />
          {hiddenUnknownCount > 0 && (
            <p data-testid="kalshi-unknown-start-count" className="font-mono text-[11px] text-zinc-600">
              {hiddenUnknownCount} additional trade{hiddenUnknownCount === 1 ? "" : "s"} hidden -- event start time could not be verified.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default KalshiBigMoney;

